/**
 * Virtual Try-On Widget
 *
 * Self-contained ES5-compatible (no build step) widget that adds a
 * "Try On" button to Shopify product pages and walks the customer through
 * a 3-step fitting experience:
 *
 *   Step 1 — Photo capture (webcam or upload) + body measurements
 *   Step 2 — AI processing (polls backend, animated progress)
 *   Step 3 — Try-on result + per-size fit assessment + add-to-cart
 *
 * Configuration is read from data-* attributes on the mount element.
 * See virtual-tryon.liquid for the full attribute list.
 */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────────────────────

  function el(tag, cls, html) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (html) node.innerHTML = html;
    return node;
  }

  function resizeImageToBase64(file, maxW, maxH, quality, callback) {
    var reader = new FileReader();
    reader.onload = function (e) {
      var img = new Image();
      img.onload = function () {
        var w = img.width, h = img.height;
        var scale = Math.min(maxW / w, maxH / h, 1);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        callback(canvas.toDataURL('image/jpeg', quality || 0.88));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  // ── Widget Class ───────────────────────────────────────────────────────────

  function VirtualTryOn(mount) {
    this.mount         = mount;
    this.apiBase       = (mount.dataset.apiBase || '').replace(/\/$/, '');
    var img            = mount.dataset.productImage || '';
    this.productImage  = img.startsWith('//') ? 'https:' + img : img;
    this.productTitle  = mount.dataset.productTitle  || 'this item';
    this.productType   = mount.dataset.productType   || '';
    this.variantId     = mount.dataset.variantId     || '';
    this.category      = mount.dataset.category      || 'upper_body';
    this.sizes         = (mount.dataset.sizes || 'S,M,L').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    // State
    this.step             = 1;
    this.stream           = null;  // webcam MediaStream
    this.photoDataUrl     = null;  // captured / uploaded photo
    this.fitData          = null;  // { measurements, recommendedSize, fitAssessments }
    this.currentSize      = this.sizes[0] || 'M';
    this.tryonCache       = {};    // size → imageUrl
    this.baseResultImg    = null;  // original Image object from AI result
    this.predictionId     = null;
    this.pollTimer        = null;
    this.progressTimer    = null;
    this.progressValue    = 0;
    this.bodyShapeValue   = 'regular'; // SVG shape picker value

    this._render();
    this._bind();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  VirtualTryOn.prototype._render = function () {
    // Inject trigger button right after the mount element
    this.triggerBtn = el('button', 'vt-trigger-btn',
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
      'Virtual Try-On'
    );
    this.mount.appendChild(this.triggerBtn);

    // Build overlay + modal
    this.overlay = el('div', 'vt-overlay');
    this.overlay.innerHTML = this._modalHTML();
    document.body.appendChild(this.overlay);

    // Cache frequently used DOM nodes
    this.panelUpload   = this.overlay.querySelector('#vt-panel-1');
    this.panelLoading  = this.overlay.querySelector('#vt-panel-2');
    this.panelResult   = this.overlay.querySelector('#vt-panel-3');

    this.videoEl       = this.overlay.querySelector('#vt-video');
    this.canvasEl      = this.overlay.querySelector('#vt-canvas');
    this.previewImg    = this.overlay.querySelector('#vt-preview-img');
    this.fileInput     = this.overlay.querySelector('#vt-file-input');

    this.heightInput      = this.overlay.querySelector('#vt-height');
    this.weightInput      = this.overlay.querySelector('#vt-weight');
    this.genderSelect     = this.overlay.querySelector('#vt-gender');
    this.shapePicker      = this.overlay.querySelector('#vt-shape-picker');

    this.tryBtn        = this.overlay.querySelector('#vt-try-btn');
    this.cameraBtn     = this.overlay.querySelector('#vt-camera-btn');
    this.uploadBtn     = this.overlay.querySelector('#vt-upload-btn');
    this.captureBtn    = this.overlay.querySelector('#vt-capture-btn');
    this.retakeBtn     = this.overlay.querySelector('#vt-retake-btn');

    this.loadingText   = this.overlay.querySelector('#vt-loading-text');
    this.loadingSub    = this.overlay.querySelector('#vt-loading-sub');
    this.progressFill  = this.overlay.querySelector('#vt-progress-fill');

    this.resultImg     = this.overlay.querySelector('#vt-result-img');
    this.resultCanvas  = this.overlay.querySelector('#vt-result-canvas');
    this.fitBadge      = this.overlay.querySelector('#vt-fit-badge');
    this.sizeTabs      = this.overlay.querySelector('#vt-size-tabs');
    this.fitCard       = this.overlay.querySelector('#vt-fit-card');
    this.measureBreak  = this.overlay.querySelector('#vt-measure-breakdown');
    this.cartBtn       = this.overlay.querySelector('#vt-cart-btn');
    this.restartBtn    = this.overlay.querySelector('#vt-restart-btn');
    this.errorPanel    = this.overlay.querySelector('#vt-error');

    this.stepEls       = this.overlay.querySelectorAll('.vt-step');
  };

  VirtualTryOn.prototype._modalHTML = function () {
    var sizeOpts = this.sizes.map(function (s) {
      return '<option value="' + s + '">' + s + '</option>';
    }).join('');

    return '' +
      '<div class="vt-modal">' +
        '<div class="vt-modal-header">' +
          '<div>' +
            '<h2 class="vt-modal-title">Virtual Try-On</h2>' +
            '<p class="vt-modal-subtitle">See how it fits before you buy</p>' +
          '</div>' +
          '<button class="vt-close-btn" id="vt-close-btn" aria-label="Close">&#x2715;</button>' +
        '</div>' +

        '<div class="vt-steps">' +
          '<div class="vt-step vt-step-active">' +
            '<span class="vt-step-num">1</span>' +
            '<span>Your Photo</span>' +
          '</div>' +
          '<div class="vt-step-divider"></div>' +
          '<div class="vt-step">' +
            '<span class="vt-step-num">2</span>' +
            '<span>Processing</span>' +
          '</div>' +
          '<div class="vt-step-divider"></div>' +
          '<div class="vt-step">' +
            '<span class="vt-step-num">3</span>' +
            '<span>Result</span>' +
          '</div>' +
        '</div>' +

        /* ── Step 1 ── */
        '<div class="vt-panel" id="vt-panel-1">' +
          '<div class="vt-upload-grid">' +

            '<div>' +
              '<div class="vt-photo-area" id="vt-photo-area">' +
                '<div class="vt-photo-placeholder" id="vt-placeholder">' +
                  '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>' +
                  '<p>Upload a full-body photo<br>or use your camera</p>' +
                '</div>' +
                '<video id="vt-video" autoplay playsinline style="display:none;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;"></video>' +
                '<canvas id="vt-canvas" style="display:none;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;"></canvas>' +
                '<img id="vt-preview-img" alt="Your photo" style="display:none;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" />' +
                '<div class="vt-guide-overlay" id="vt-guide-overlay" style="display:none;"><div class="vt-guide-silhouette"></div></div>' +
                '<div class="vt-photo-controls">' +
                  '<button class="vt-btn-sm vt-btn-outline" id="vt-camera-btn">Camera</button>' +
                  '<button class="vt-btn-sm vt-btn-outline" id="vt-upload-btn">Upload</button>' +
                  '<input type="file" id="vt-file-input" accept="image/*" style="display:none;" />' +
                  '<button class="vt-btn-sm vt-btn-solid" id="vt-capture-btn" style="display:none;">Take Photo</button>' +
                  '<button class="vt-btn-sm vt-btn-outline" id="vt-retake-btn" style="display:none;">Retake</button>' +
                '</div>' +
              '</div>' +
              '<div class="vt-tip" style="margin-top:10px;">' +
                '<strong>Best results:</strong> Stand straight, arms slightly away from body, in good lighting.' +
              '</div>' +
            '</div>' +

            '<div class="vt-measurements">' +
              '<h3>Your Measurements</h3>' +
              '<div class="vt-field">' +
                '<label for="vt-gender">Gender</label>' +
                '<select id="vt-gender"><option value="women">Women</option><option value="men">Men</option></select>' +
              '</div>' +
              '<div class="vt-field">' +
                '<label>Body Shape</label>' +
                '<div class="vt-shape-picker" id="vt-shape-picker">' +
                  '<button type="button" class="vt-shape-btn vt-shape-active" data-shape="regular">' +
                    '<svg viewBox="0 0 28 44" width="28" height="44" fill="currentColor">' +
                      '<ellipse cx="14" cy="5" rx="4" ry="4.5"/>' +
                      '<path d="M8,12 C6,14 6,28 7,38 L21,38 C22,28 22,14 20,12 C18,10 10,10 8,12Z"/>' +
                    '</svg>' +
                    '<span>Regular</span>' +
                  '</button>' +
                  '<button type="button" class="vt-shape-btn" data-shape="athletic">' +
                    '<svg viewBox="0 0 28 44" width="28" height="44" fill="currentColor">' +
                      '<ellipse cx="14" cy="5" rx="4" ry="4.5"/>' +
                      '<path d="M5,12 C4,15 7,26 8,38 L20,38 C21,26 24,15 23,12 C20,9 8,9 5,12Z"/>' +
                    '</svg>' +
                    '<span>Athletic</span>' +
                  '</button>' +
                  '<button type="button" class="vt-shape-btn" data-shape="muscular">' +
                    '<svg viewBox="0 0 28 44" width="28" height="44" fill="currentColor">' +
                      '<ellipse cx="14" cy="5" rx="4" ry="4.5"/>' +
                      '<path d="M3,11 C2,15 6,25 8,38 L20,38 C22,25 26,15 25,11 C22,8 6,8 3,11Z"/>' +
                      '<path d="M7,17 Q14,21 21,17" fill="none" stroke="white" stroke-width="1.2" opacity="0.4"/>' +
                    '</svg>' +
                    '<span>Muscular</span>' +
                  '</button>' +
                  '<button type="button" class="vt-shape-btn" data-shape="pear">' +
                    '<svg viewBox="0 0 28 44" width="28" height="44" fill="currentColor">' +
                      '<ellipse cx="14" cy="5" rx="4" ry="4.5"/>' +
                      '<path d="M10,12 C9,14 8,22 5,38 L23,38 C20,22 19,14 18,12 C16,10 12,10 10,12Z"/>' +
                    '</svg>' +
                    '<span>Pear</span>' +
                  '</button>' +
                  '<button type="button" class="vt-shape-btn" data-shape="apple">' +
                    '<svg viewBox="0 0 28 44" width="28" height="44" fill="currentColor">' +
                      '<ellipse cx="14" cy="5" rx="4" ry="4.5"/>' +
                      '<path d="M8,12 C4,16 3,22 6,38 L22,38 C25,22 24,16 20,12 C17,10 11,10 8,12Z"/>' +
                    '</svg>' +
                    '<span>Apple</span>' +
                  '</button>' +
                '</div>' +
              '</div>' +
              '<div class="vt-field">' +
                '<label for="vt-height">Height (cm)</label>' +
                '<input type="number" id="vt-height" placeholder="e.g. 170" min="140" max="210" />' +
              '</div>' +
              '<div class="vt-field">' +
                '<label for="vt-weight">Weight (kg)</label>' +
                '<input type="number" id="vt-weight" placeholder="e.g. 65" min="40" max="200" />' +
              '</div>' +
              '<p class="vt-privacy-note">' +
                '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
                'Used only to calculate fit — never stored.' +
              '</p>' +
            '</div>' +
          '</div>' +
          '<button class="vt-btn-primary" id="vt-try-btn" disabled>Try It On</button>' +
        '</div>' +

        /* ── Step 2 ── */
        '<div class="vt-panel vt-panel-hidden" id="vt-panel-2">' +
          '<div class="vt-loading">' +
            '<div class="vt-spinner"></div>' +
            '<p class="vt-loading-text" id="vt-loading-text">Preparing your fitting room…</p>' +
            '<p class="vt-loading-sub" id="vt-loading-sub">This takes about 30 seconds</p>' +
            '<div class="vt-progress-track"><div class="vt-progress-fill" id="vt-progress-fill"></div></div>' +
          '</div>' +
        '</div>' +

        /* ── Step 3 ── */
        '<div class="vt-panel vt-panel-hidden" id="vt-panel-3">' +
          '<div id="vt-error" style="display:none;" class="vt-error">' +
            '<span class="vt-error-icon">&#x26A0;&#xFE0F;</span>' +
            '<p id="vt-error-msg">Something went wrong. Please try again.</p>' +
            '<button class="vt-btn-sm vt-btn-outline" id="vt-err-retry-btn">Try Again</button>' +
          '</div>' +
          '<div id="vt-result-content">' +
            '<div class="vt-result-grid">' +
              '<div class="vt-result-image-wrap">' +
                '<canvas id="vt-result-canvas" style="width:100%;height:auto;display:none;"></canvas>' +
                '<img id="vt-result-img" alt="Virtual try-on result" />' +
                '<div class="vt-fit-badge" id="vt-fit-badge"></div>' +
              '</div>' +
              '<div class="vt-result-panel">' +
                '<div>' +
                  '<p class="vt-size-label">Select size to preview fit</p>' +
                  '<div class="vt-size-tabs" id="vt-size-tabs"></div>' +
                '</div>' +
                '<div id="vt-fit-card" class="vt-fit-card">' +
                  '<p class="vt-fit-card-label" id="vt-fit-label">Perfect Fit</p>' +
                  '<p class="vt-fit-card-desc" id="vt-fit-desc">This size matches your measurements.</p>' +
                '</div>' +
                '<div class="vt-measurements-breakdown" id="vt-measure-breakdown">' +
                  '<h4>Estimated measurements</h4>' +
                  '<div id="vt-measure-rows"></div>' +
                '</div>' +
                '<div class="vt-result-actions">' +
                  '<button class="vt-btn-cart" id="vt-cart-btn">Add to Cart</button>' +
                  '<button class="vt-btn-restart" id="vt-restart-btn">&#x21BA; Try a Different Photo</button>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +

      '</div>';
  };

  // ── Event Binding ──────────────────────────────────────────────────────────

  VirtualTryOn.prototype._bind = function () {
    var self = this;

    this.triggerBtn.addEventListener('click', function () { self._open(); });

    this.overlay.querySelector('#vt-close-btn').addEventListener('click', function () { self._close(); });
    this.overlay.addEventListener('click', function (e) { if (e.target === self.overlay) self._close(); });

    this.cameraBtn.addEventListener('click', function () { self._startCamera(); });
    this.uploadBtn.addEventListener('click', function () { self.fileInput.click(); });
    this.fileInput.addEventListener('change', function (e) {
      if (e.target.files[0]) self._loadFile(e.target.files[0]);
    });
    this.captureBtn.addEventListener('click', function () { self._captureFrame(); });
    this.retakeBtn.addEventListener('click',  function () { self._resetPhoto(); });

    this.heightInput.addEventListener('input', function () { self._checkTryBtnState(); });
    this.weightInput.addEventListener('input', function () { self._checkTryBtnState(); });

    // Body shape SVG picker
    var shapeBtns = this.overlay.querySelectorAll('.vt-shape-btn');
    for (var si = 0; si < shapeBtns.length; si++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var all = self.overlay.querySelectorAll('.vt-shape-btn');
          for (var j = 0; j < all.length; j++) all[j].classList.remove('vt-shape-active');
          btn.classList.add('vt-shape-active');
          self.bodyShapeValue = btn.dataset.shape || 'regular';
        });
      })(shapeBtns[si]);
    }

    this.tryBtn.addEventListener('click', function () { self._startTryOn(); });
    this.restartBtn.addEventListener('click', function () { self._resetToStep1(); });
    this.overlay.querySelector('#vt-err-retry-btn').addEventListener('click', function () { self._resetToStep1(); });
    this.cartBtn.addEventListener('click', function () { self._addToCart(); });

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') self._close(); });
  };

  // ── Open / Close ───────────────────────────────────────────────────────────

  VirtualTryOn.prototype._open = function () {
    this.overlay.classList.add('vt-visible');
    document.body.style.overflow = 'hidden';
  };

  VirtualTryOn.prototype._close = function () {
    this.overlay.classList.remove('vt-visible');
    document.body.style.overflow = '';
    this._stopCamera();
    this._stopPolling();
    this._stopProgress();
  };

  // ── Step navigation ────────────────────────────────────────────────────────

  VirtualTryOn.prototype._goToStep = function (n) {
    this.step = n;
    this.panelUpload.classList.toggle('vt-panel-hidden', n !== 1);
    this.panelLoading.classList.toggle('vt-panel-hidden', n !== 2);
    this.panelResult.classList.toggle('vt-panel-hidden', n !== 3);

    var stepEls = this.stepEls;
    for (var i = 0; i < stepEls.length; i++) {
      stepEls[i].classList.remove('vt-step-active', 'vt-step-done');
      if (i + 1 < n)  stepEls[i].classList.add('vt-step-done');
      if (i + 1 === n) stepEls[i].classList.add('vt-step-active');
    }
  };

  // ── Camera / Photo ─────────────────────────────────────────────────────────

  VirtualTryOn.prototype._startCamera = function () {
    var self = this;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Camera not supported in this browser. Please upload a photo instead.');
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: 768, height: 1024 } })
      .then(function (stream) {
        self.stream = stream;
        self.videoEl.srcObject = stream;
        self.videoEl.style.display = '';
        self.overlay.querySelector('#vt-placeholder').style.display = 'none';
        self.overlay.querySelector('#vt-guide-overlay').style.display = '';
        self.cameraBtn.style.display   = 'none';
        self.uploadBtn.style.display   = 'none';
        self.captureBtn.style.display  = '';
      })
      .catch(function (err) {
        console.error('[vt] camera error', err);
        alert('Could not access camera. Please upload a photo instead.');
      });
  };

  VirtualTryOn.prototype._captureFrame = function () {
    var video  = this.videoEl;
    var canvas = this.canvasEl;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    this.photoDataUrl = canvas.toDataURL('image/jpeg', 0.88);

    this._stopCamera();
    this.videoEl.style.display = 'none';
    canvas.style.display = '';
    this.overlay.querySelector('#vt-guide-overlay').style.display = 'none';
    this.captureBtn.style.display = 'none';
    this.retakeBtn.style.display  = '';
    this._checkTryBtnState();
  };

  VirtualTryOn.prototype._loadFile = function (file) {
    var self = this;
    resizeImageToBase64(file, 768, 1024, 0.88, function (dataUrl) {
      self.photoDataUrl = dataUrl;
      self.previewImg.src = dataUrl;
      self.previewImg.style.display = '';
      self.overlay.querySelector('#vt-placeholder').style.display = 'none';
      self.cameraBtn.style.display  = 'none';
      self.uploadBtn.style.display  = 'none';
      self.retakeBtn.style.display  = '';
      self._checkTryBtnState();
    });
  };

  VirtualTryOn.prototype._resetPhoto = function () {
    this._stopCamera();
    this.photoDataUrl = null;
    this.previewImg.style.display = 'none';
    this.previewImg.src = '';
    this.canvasEl.style.display = 'none';
    this.videoEl.style.display  = 'none';
    this.overlay.querySelector('#vt-placeholder').style.display = '';
    this.overlay.querySelector('#vt-guide-overlay').style.display = 'none';
    this.cameraBtn.style.display  = '';
    this.uploadBtn.style.display  = '';
    this.retakeBtn.style.display  = 'none';
    this.captureBtn.style.display = 'none';
    this.fileInput.value = '';
    this._checkTryBtnState();
  };

  VirtualTryOn.prototype._stopCamera = function () {
    if (this.stream) {
      this.stream.getTracks().forEach(function (t) { t.stop(); });
      this.stream = null;
    }
  };

  VirtualTryOn.prototype._checkTryBtnState = function () {
    var hasPhoto = !!this.photoDataUrl;
    var hasH = this.heightInput.value && Number(this.heightInput.value) >= 140;
    var hasW = this.weightInput.value && Number(this.weightInput.value) >= 30;
    this.tryBtn.disabled = !(hasPhoto && hasH && hasW);
  };

  // ── Try-On Pipeline ────────────────────────────────────────────────────────

  VirtualTryOn.prototype._startTryOn = function () {
    var self = this;

    this._goToStep(2);
    this._animateProgress([
      { pct: 15, msg: 'Analysing your photo\u2026',     sub: 'Detecting body pose' },
      { pct: 35, msg: 'Extracting garment\u2026',       sub: 'Cleaning up product image' },
      { pct: 60, msg: 'Fitting the garment\u2026',      sub: 'AI is rendering the try-on' },
      { pct: 80, msg: 'Adding finishing touches\u2026', sub: 'Almost there' },
      { pct: 95, msg: 'Wrapping up\u2026',              sub: '' },
    ]);

    // Fetch fit scores (best-effort) and kick off the try-on in parallel
    // Fit score failure must NOT block the try-on
    var fitPromise = this._fetchFitScore().catch(function (err) {
      console.warn('[vt] fit score unavailable:', err.message);
      return null;
    });
    var tryonPromise = this._startTryonPrediction();

    Promise.all([fitPromise, tryonPromise])
      .then(function (results) {
        self.fitData = results[0];      // may be null if fit-score failed
        self.predictionId = results[1];
        self._pollPrediction();
      })
      .catch(function (err) {
        console.error('[vt] start error', err);
        self._showError(err.message || 'Failed to start try-on. Please try again.');
      });
  };

  VirtualTryOn.prototype._fetchFitScore = function () {
    var self = this;
    return fetch(this.apiBase + '/api/fit-score', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        heightCm:  Number(this.heightInput.value),
        weightKg:  Number(this.weightInput.value),
        gender:    this.genderSelect.value,
        bodyShape: this.bodyShapeValue || 'regular',
        sizes:     this.sizes,
        category:  this.category,
      }),
    }).then(function (r) {
      if (!r.ok) throw new Error('Fit score calculation failed');
      return r.json();
    });
  };

  VirtualTryOn.prototype._startTryonPrediction = function () {
    var self = this;
    return fetch(this.apiBase + '/api/tryon/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        personImageBase64: this.photoDataUrl,
        garmentImageUrl:   this.productImage,
        garmentDescription: this.productTitle + ' ' + this.productType,
        category:          this.category,
      }),
    }).then(function (r) {
      if (!r.ok) throw new Error('Could not start try-on. Please try again.');
      return r.json();
    }).then(function (data) {
      if (!data.predictionId) throw new Error('No prediction ID returned.');
      return data.predictionId;
    });
  };

  VirtualTryOn.prototype._pollPrediction = function () {
    var self = this;
    var attempts = 0;
    var maxAttempts = 40; // 40 × 3 s = 2 min max

    this.pollTimer = setInterval(function () {
      attempts++;
      if (attempts > maxAttempts) {
        self._stopPolling();
        self._showError('The try-on is taking longer than expected. Please try again.');
        return;
      }

      fetch(self.apiBase + '/api/tryon/status/' + self.predictionId)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status === 'succeeded' && data.imageUrl) {
            self._stopPolling();
            self._stopProgress();
            self._showResult(data.imageUrl);
          } else if (data.status === 'failed' || data.status === 'canceled') {
            self._stopPolling();
            self._showError(data.error || 'Try-on failed. Please try again.');
          }
          // 'starting' | 'processing' → keep polling
        })
        .catch(function (err) {
          console.error('[vt] poll error', err);
          // Don't stop polling on transient network errors
        });
    }, 3000);
  };

  VirtualTryOn.prototype._stopPolling = function () {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  };

  // ── Progress animation ─────────────────────────────────────────────────────

  VirtualTryOn.prototype._animateProgress = function (stages) {
    var self = this;
    var idx = 0;
    this.progressFill.style.width = '5%';

    this.progressTimer = setInterval(function () {
      if (idx < stages.length) {
        var s = stages[idx];
        self.progressFill.style.width = s.pct + '%';
        self.loadingText.textContent = s.msg;
        self.loadingSub.textContent  = s.sub;
        idx++;
      }
    }, 7000);
  };

  VirtualTryOn.prototype._stopProgress = function () {
    if (this.progressTimer) { clearInterval(this.progressTimer); this.progressTimer = null; }
    this.progressFill.style.width = '100%';
  };

  // ── Result display ─────────────────────────────────────────────────────────

  VirtualTryOn.prototype._showResult = function (imageUrl) {
    var self = this;

    this._goToStep(3);
    this.errorPanel.style.display = 'none';
    this.overlay.querySelector('#vt-result-content').style.display = '';

    // Load the AI result into an Image object for canvas warping
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self.baseResultImg = img;

      // Render size tabs
      self._renderSizeTabs();

      // Show fit for the recommended size if available, otherwise first size
      var bestSize = (self.fitData && self.fitData.recommendedSize) || self.sizes[0];
      var startSize = self.sizes.indexOf(bestSize) >= 0 ? bestSize : self.sizes[0];
      self._selectSize(startSize);

      // Render body measurements breakdown
      if (self.fitData && self.fitData.measurements) {
        self._renderMeasurements(self.fitData.measurements);
      }
    };
    img.onerror = function () {
      // Fallback: show image directly without warping
      self.resultImg.src = imageUrl;
      self.resultImg.style.display = '';
      self.resultCanvas.style.display = 'none';
      self._renderSizeTabs();
      var bestSize = (self.fitData && self.fitData.recommendedSize) || self.sizes[0];
      self._selectSize(bestSize);
    };
    img.src = imageUrl;
  };

  VirtualTryOn.prototype._renderSizeTabs = function () {
    var self = this;
    var rec = this.fitData && this.fitData.recommendedSize;
    this.sizeTabs.innerHTML = '';
    this.sizes.forEach(function (size) {
      var tab = el('button', 'vt-size-tab', size);
      tab.dataset.size = size;
      if (size === rec) tab.classList.add('vt-size-recommended');
      tab.addEventListener('click', function () { self._selectSize(size); });
      self.sizeTabs.appendChild(tab);
    });
  };

  // Size order for calculating visual fit offset
  VirtualTryOn.SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  VirtualTryOn.prototype._selectSize = function (size) {
    var self = this;
    this.currentSize = size;

    // Update tab active state
    var tabs = this.sizeTabs.querySelectorAll('.vt-size-tab');
    tabs.forEach(function (t) {
      t.classList.toggle('vt-size-active', t.dataset.size === size);
    });

    // Update fit card
    if (this.fitData && this.fitData.fitAssessments && this.fitData.fitAssessments[size]) {
      var fit = this.fitData.fitAssessments[size];
      this.overlay.querySelector('#vt-fit-label').textContent = fit.label;
      this.overlay.querySelector('#vt-fit-desc').textContent  = fit.description;
      this.fitCard.style.borderLeftColor = fit.color;

      this.fitBadge.textContent   = fit.label;
      this.fitBadge.style.background = fit.color;
      this.fitBadge.style.display = '';
    }

    // Render warped image for this size
    this._renderWarpedResult(size);
  };

  /**
   * Independent garment layer rendering — the core of size visualization.
   *
   * The body (AI result) is ALWAYS drawn at 100% — it is never distorted.
   * The garment zone is extracted as a separate layer, scaled from its center
   * (dilation for larger sizes, contraction for smaller), then composited on top.
   *
   * Larger sizes: garment expands outward + drop shadow simulates fabric
   *   floating away from the body, conveying a relaxed/loose fit.
   * Smaller sizes: garment contracts inward, no shadow (tight against body).
   *
   * Vertical edges of the overlay are feathered so the garment zone blends
   * smoothly into the head and legs below/above the zone.
   */
  VirtualTryOn.prototype._renderWarpedResult = function (size) {
    var baseImg = this.baseResultImg;
    if (!baseImg) return;

    var rec = this.fitData && this.fitData.recommendedSize;
    var steps = 0;
    if (rec) {
      var recIdx = VirtualTryOn.SIZE_ORDER.indexOf(rec.toUpperCase());
      var sizeIdx = VirtualTryOn.SIZE_ORDER.indexOf(size.toUpperCase());
      if (recIdx >= 0 && sizeIdx >= 0) steps = sizeIdx - recIdx;
    }

    var w = baseImg.naturalWidth;
    var h = baseImg.naturalHeight;
    var canvas = this.resultCanvas;
    canvas.width  = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');

    // Layer 1 — base body: always at 100%, never distorted
    ctx.drawImage(baseImg, 0, 0);
    canvas.style.display = '';
    this.resultImg.style.display = 'none';

    if (steps === 0) return; // perfect fit — base image is already correct

    // ── Garment zone boundaries (fraction of image height) ────────────────────
    var cat = this.category || 'upper_body';
    var zoneTop, zoneBot;
    if (cat === 'lower_body') {
      zoneTop = Math.round(h * 0.38);
      zoneBot = Math.round(h * 0.88);
    } else if (cat === 'dresses') {
      zoneTop = Math.round(h * 0.15);
      zoneBot = Math.round(h * 0.88);
    } else {
      zoneTop = Math.round(h * 0.15);
      zoneBot = Math.round(h * 0.62);
    }
    var zoneH = zoneBot - zoneTop;

    // ── Dilation factors ──────────────────────────────────────────────────────
    // Each size step dilates the garment layer by 3.5% horizontally, 1.8% vertically.
    // Clamped to ±3 steps so extreme sizes still look plausible.
    var clampedSteps = Math.max(-3, Math.min(3, steps));
    var dilateX = clampedSteps * 0.035;
    var dilateY = clampedSteps * 0.018;

    var dstW = Math.round(w * (1 + dilateX));
    var dstH = Math.round(zoneH * (1 + dilateY));

    // Centre the scaled zone over the original garment zone
    var dstX = Math.round(w / 2 - dstW / 2);
    var dstY = Math.round(zoneTop + zoneH / 2 - dstH / 2);

    // ── Offscreen canvas: extract + scale the garment zone ────────────────────
    var off = document.createElement('canvas');
    off.width  = Math.max(1, dstW);
    off.height = Math.max(1, dstH);
    var offCtx = off.getContext('2d');
    offCtx.drawImage(baseImg, 0, zoneTop, w, zoneH, 0, 0, dstW, dstH);

    // ── Feather vertical edges so the overlay blends into head/legs ───────────
    var feather = Math.round(zoneH * 0.10); // 10% of zone height
    offCtx.globalCompositeOperation = 'destination-in';

    // Top fade
    var topGrad = offCtx.createLinearGradient(0, 0, 0, feather);
    topGrad.addColorStop(0, 'rgba(0,0,0,0)');
    topGrad.addColorStop(1, 'rgba(0,0,0,1)');
    offCtx.fillStyle = topGrad;
    offCtx.fillRect(0, 0, dstW, feather);

    // Bottom fade
    var botGrad = offCtx.createLinearGradient(0, dstH - feather, 0, dstH);
    botGrad.addColorStop(0, 'rgba(0,0,0,1)');
    botGrad.addColorStop(1, 'rgba(0,0,0,0)');
    offCtx.fillStyle = botGrad;
    offCtx.fillRect(0, dstH - feather, dstW, feather);

    offCtx.globalCompositeOperation = 'source-over';

    // ── Shadow for loose fit: simulates fabric floating away from body ─────────
    if (steps > 0) {
      ctx.shadowBlur    = steps * 7;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = steps * 2;
      ctx.shadowColor   = 'rgba(0,0,0,' + Math.min(0.35, steps * 0.10) + ')';
    }

    // Layer 2 — garment overlay (scaled, feathered, with optional shadow)
    ctx.drawImage(off, dstX, dstY);

    // Reset shadow state
    ctx.shadowBlur    = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowColor   = 'transparent';
  };

  VirtualTryOn.prototype._renderMeasurements = function (m) {
    var rows = this.overlay.querySelector('#vt-measure-rows');
    rows.innerHTML =
      '<div class="vt-measurements-row"><span>Chest</span><span>' + m.chest + ' cm</span></div>' +
      '<div class="vt-measurements-row"><span>Waist</span><span>' + m.waist + ' cm</span></div>' +
      '<div class="vt-measurements-row"><span>Hip</span><span>'   + m.hip   + ' cm</span></div>' +
      '<div class="vt-measurements-row"><span>BMI</span><span>'   + m.bmi   + '</span></div>';
  };

  // ── Error display ──────────────────────────────────────────────────────────

  VirtualTryOn.prototype._showError = function (msg) {
    this._goToStep(3);
    this.errorPanel.style.display = '';
    this.overlay.querySelector('#vt-error-msg').textContent = msg;
    this.overlay.querySelector('#vt-result-content').style.display = 'none';
    this._stopProgress();
  };

  // ── Reset ──────────────────────────────────────────────────────────────────

  VirtualTryOn.prototype._resetToStep1 = function () {
    this._stopPolling();
    this._stopProgress();
    this.photoDataUrl   = null;
    this.fitData        = null;
    this.predictionId   = null;
    this.tryonCache     = {};
    this.baseResultImg  = null;
    this.progressFill.style.width = '0%';
    this.loadingText.textContent = 'Preparing your fitting room\u2026';
    this._resetPhoto();
    this.heightInput.value = '';
    this.weightInput.value = '';
    this.tryBtn.disabled = true;
    this.errorPanel.style.display = 'none';
    this.resultCanvas.style.display = 'none';
    this.resultImg.style.display = '';
    this._goToStep(1);
  };

  // ── Add to Cart ────────────────────────────────────────────────────────────

  VirtualTryOn.prototype._addToCart = function () {
    // Try to find the Shopify variant for the selected size and add it
    // Falls back to just closing the modal if the variant can't be found.
    var self = this;
    var selectedSize = this.currentSize;

    // Try to find variant ID from the product form on the page
    var variantSelect = document.querySelector('select[name="id"]');
    var variantRadio  = document.querySelector('input[name="id"]:checked');
    var variantId     = null;

    // Try to match by option label
    if (variantSelect) {
      var opts = variantSelect.options;
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].text.toLowerCase().indexOf(selectedSize.toLowerCase()) >= 0) {
          variantId = opts[i].value;
          break;
        }
      }
    }

    if (!variantId && variantRadio) variantId = variantRadio.value;
    if (!variantId) variantId = this.variantId;

    if (!variantId) {
      // Couldn't determine variant — close modal and let customer use normal add-to-cart
      this._close();
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        self._close();
        // Trigger Shopify cart update event for themes that listen to it
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Navigate to cart if the theme doesn't auto-open a drawer
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.error('[vt] add to cart error', err);
        self._close();
      });
  };

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    var mounts = document.querySelectorAll('[data-vt-mount]');
    for (var i = 0; i < mounts.length; i++) {
      new VirtualTryOn(mounts[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
